import { useEffect, useState } from "react";
import { useAdvancedStats, useTrades, useOpenPositions } from "@/hooks/useData";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { fmtMoney, fmtSignedMoney, fmtPct, fmtNum, pnlClass, fmtDurationHM, fmtDate } from "@/lib/format";
import { ChevronDown, ChevronUp, Info, ArrowUpRight, ArrowDownRight } from "lucide-react";

/* ------------------------------------------------------------- helpers */

function useCollapsed(id, defaultOpen = true) {
  const key = `flux:panel:${id}`;
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      return v === null ? defaultOpen : v === "1";
    } catch {
      return defaultOpen;
    }
  });
  useEffect(() => {
    try { localStorage.setItem(key, open ? "1" : "0"); } catch { /* ignore */ }
  }, [key, open]);
  return [open, setOpen];
}

function Panel({ id, title, subtitle, right, defaultOpen = true, children, testid }) {
  const [open, setOpen] = useCollapsed(id, defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen}
      className="rounded-2xl border bg-card" data-testid={testid}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 sm:px-5">
        <CollapsibleTrigger className="flex flex-1 items-center gap-2 text-left" data-testid={`${testid}-toggle`}>
          {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          <div>
            <div className="font-display text-sm font-semibold">{title}</div>
            {subtitle && <div className="text-[11px] text-muted-foreground">{subtitle}</div>}
          </div>
        </CollapsibleTrigger>
        {right}
      </div>
      <CollapsibleContent>
        <div className="border-t px-4 py-4 sm:px-5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function Metric({ label, tip, value, cls }) {
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
        {tip && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help"><Info className="h-2.5 w-2.5 opacity-60" /></span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[240px] text-xs">{tip}</TooltipContent>
          </Tooltip>
        )}
      </div>
      <div className={cn("text-sm font-semibold tabular-nums", cls)}>{value}</div>
    </div>
  );
}

function MetricGrid({ children }) {
  return <div className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 lg:grid-cols-4">{children}</div>;
}

function SubTabs({ tabs, active, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-1 rounded-full bg-surface-2 p-1 w-fit">
      {tabs.map((t) => (
        <button key={t.v} type="button" onClick={() => onChange(t.v)}
          className={cn("rounded-full px-3 py-1 text-xs font-medium transition-colors",
            active === t.v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}
          data-testid={`${testidPrefix}-${t.v}`}>
          {t.l}
        </button>
      ))}
    </div>
  );
}

function BucketBars({ rows, currency }) {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.profit)));
  return (
    <div className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.label} className="flex items-center gap-3 text-xs" data-testid={`bucket-${r.label}`}>
          <span className="w-20 shrink-0 font-medium">{r.label}</span>
          <div className="relative h-5 flex-1 rounded bg-surface-2">
            <div className={cn("absolute inset-y-0 left-0 rounded",
              r.profit >= 0 ? "bg-[hsl(var(--profit))]/25" : "bg-[hsl(var(--loss))]/25")}
              style={{ width: `${(Math.abs(r.profit) / max) * 100}%` }} />
            <span className={cn("absolute inset-y-0 left-2 flex items-center text-[11px] font-semibold tabular-nums", pnlClass(r.profit))}>
              {fmtSignedMoney(r.profit, currency)}
            </span>
          </div>
          <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">{r.trades} tr</span>
          <span className="w-14 shrink-0 text-right tabular-nums text-muted-foreground">{r.win_rate}%</span>
        </div>
      ))}
    </div>
  );
}

function TradeRef({ t, currency, showPips }) {
  if (!t) return <span className="text-muted-foreground">-</span>;
  return (
    <span className="tabular-nums">
      <span className={pnlClass(t.net)}>{showPips ? `${t.pips > 0 ? "+" : ""}${t.pips} pips` : fmtSignedMoney(t.net, currency)}</span>
      <span className="text-muted-foreground"> · {t.symbol} · {fmtDate(t.date)}</span>
    </span>
  );
}

/* ------------------------------------------------------------- blocks */

function StatsStrip({ strip, currency }) {
  const items = [
    { l: "Balance", v: fmtMoney(strip.balance, currency) },
    { l: "Equity", v: fmtMoney(strip.equity, currency) },
    { l: "Gain", v: fmtPct(strip.gain_pct, true), cls: pnlClass(strip.gain_pct) },
    { l: "Abs. gain", v: fmtSignedMoney(strip.abs_gain, currency), cls: pnlClass(strip.abs_gain) },
    { l: "Max DD", v: `-${fmtPct(strip.max_drawdown_pct)}`, cls: "text-[hsl(var(--loss))]" },
    { l: "Trades", v: fmtNum(strip.trades) },
    { l: "Win rate", v: `${strip.win_rate}%` },
    { l: "Profit factor", v: strip.profit_factor },
    { l: "Lots", v: fmtNum(strip.total_lots, 2) },
  ];
  return (
    <div className="rounded-2xl border bg-card p-4 sm:p-5" data-testid="account-stats-strip">
      <div className="grid grid-cols-3 gap-x-6 gap-y-4 sm:grid-cols-5 lg:grid-cols-9">
        {items.map((it) => (
          <div key={it.l}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{it.l}</div>
            <div className={cn("font-display text-base font-semibold tabular-nums", it.cls)}>{it.v}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdvancedStatistics({ stats, currency }) {
  const [tab, setTab] = useState("trades");
  const [bucket, setBucket] = useState("weekday");
  const q = stats.quality;
  const r = stats.returns;
  const rk = stats.risk;

  return (
    <Panel id="advanced-statistics" title="Advanced statistics"
      subtitle="Returns, risk and trade quality for the selected range"
      testid="panel-advanced-statistics">
      <SubTabs tabs={[{ v: "trades", l: "Trades" }, { v: "performance", l: "Performance metrics" }]}
        active={tab} onChange={setTab} testidPrefix="advstat-tab" />

      {tab === "trades" ? (
        <div className="mt-4 space-y-5">
          <MetricGrid>
            <Metric label="Profit factor" tip="Gross profit divided by gross loss. Above 1 is profitable." value={q.profit_factor} />
            <Metric label="Avg win" value={fmtMoney(q.avg_win, currency)} cls="text-[hsl(var(--profit))]" />
            <Metric label="Avg loss" value={fmtMoney(q.avg_loss, currency)} cls="text-[hsl(var(--loss))]" />
            <Metric label="Avg win (pips)" value={`${q.avg_win_pips}`} />
            <Metric label="Avg loss (pips)" value={`${q.avg_loss_pips}`} />
            <Metric label="Avg trade length" tip="Mean time a position stays open." value={fmtDurationHM(q.avg_duration_minutes)} />
            <Metric label="Longest win streak" value={q.longest_win_streak} />
            <Metric label="Longest loss streak" value={q.longest_loss_streak} />
            <Metric label="Best trade" value={<TradeRef t={q.best_trade} currency={currency} />} />
            <Metric label="Worst trade" value={<TradeRef t={q.worst_trade} currency={currency} />} />
            <Metric label="Best (pips)" value={<TradeRef t={q.best_trade_pips} currency={currency} showPips />} />
            <Metric label="Worst (pips)" value={<TradeRef t={q.worst_trade_pips} currency={currency} showPips />} />
            <Metric label="Longs won" tip="Winning long trades out of all longs." value={`${q.longs_won}/${q.longs} (${q.longs_won_pct}%)`} />
            <Metric label="Shorts won" tip="Winning short trades out of all shorts." value={`${q.shorts_won}/${q.shorts} (${q.shorts_won_pct}%)`} />
            <Metric label="Gross profit" value={fmtMoney(q.gross_win, currency)} cls="text-[hsl(var(--profit))]" />
            <Metric label="Gross loss" value={fmtMoney(q.gross_loss, currency)} cls="text-[hsl(var(--loss))]" />
          </MetricGrid>

          <div>
            <SubTabs tabs={[{ v: "weekday", l: "Weekday" }, { v: "hourly", l: "Hourly" }, { v: "duration", l: "Duration" }]}
              active={bucket} onChange={setBucket} testidPrefix="advstat-bucket" />
            <div className="mt-3">
              <BucketBars rows={stats.buckets[bucket]} currency={currency} />
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <MetricGrid>
            <Metric label="TWR" tip="Time-Weighted Return: compounded return, deposit/withdrawal neutral." value={fmtPct(r.twr, true)} cls={pnlClass(r.twr)} />
            <Metric label="Absolute gain" value={fmtSignedMoney(r.abs_gain, currency)} cls={pnlClass(r.abs_gain)} />
            <Metric label="Avg daily" tip="Average of each trading day's % return, compounded off the opening balance." value={fmtPct(r.avg_daily_pct, true)} cls={pnlClass(r.avg_daily_pct)} />
            <Metric label="Avg monthly" value={fmtPct(r.avg_monthly_pct, true)} cls={pnlClass(r.avg_monthly_pct)} />
            <Metric label="AHPR" tip="Average Holding Period Return: arithmetic mean per-trade % gain." value={fmtPct(r.ahpr, true)} />
            <Metric label="GHPR" tip="Geometric Holding Period Return: compounded mean per-trade % gain." value={fmtPct(r.ghpr, true)} />
            <Metric label="Max DD (balance)" tip="Largest peak-to-valley drop measured on realized balance." value={`${fmtMoney(rk.max_dd_balance, currency)} (${rk.max_dd_balance_pct}%)`} cls="text-[hsl(var(--loss))]" />
            <Metric label="Max DD (equity)" tip="Largest drop including open floating P/L." value={`${fmtMoney(rk.max_dd_equity, currency)} (${rk.max_dd_equity_pct}%)`} cls="text-[hsl(var(--loss))]" />
            <Metric label="Z-score" tip="Runs test of streakiness. Negative = wins/losses cluster; positive = alternate." value={rk.z_score} />
            <Metric label="Z probability" value={`${rk.z_probability}%`} />
            <Metric label="Std deviation" tip="Standard deviation of per-trade net P/L." value={fmtMoney(rk.std_deviation, currency)} />
            <Metric label="Sharpe" tip="Risk-adjusted return, annualized from daily returns." value={rk.sharpe} />
            <Metric label="Expectancy" tip="Average net P/L per trade." value={fmtMoney(rk.expectancy, currency)} cls={pnlClass(rk.expectancy)} />
            <Metric label="Expectancy (pips)" value={`${rk.expectancy_pips}`} />
            <Metric label="Total pips" value={fmtNum(stats.volume.total_pips, 1)} />
            <Metric label="Total lots" value={fmtNum(stats.volume.total_lots, 2)} />
            <Metric label="Commission" value={fmtMoney(stats.volume.total_commission, currency)} />
            <Metric label="Swap" value={fmtMoney(stats.volume.total_swap, currency)} />
            <Metric label="Deposits" value={fmtMoney(stats.volume.deposits, currency)} />
            <Metric label="Withdrawals" value={fmtMoney(stats.volume.withdrawals, currency)} />
          </MetricGrid>
        </div>
      )}
    </Panel>
  );
}

function BreakdownTable({ rows, currency, keyLabel }) {
  if (!rows.length) return <p className="py-4 text-center text-xs text-muted-foreground">No trades in this period.</p>;
  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <Table className="min-w-[520px]">
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs">{keyLabel}</TableHead>
            <TableHead className="text-right text-xs">Long P/L</TableHead>
            <TableHead className="text-right text-xs">Short P/L</TableHead>
            <TableHead className="text-right text-xs">Trades</TableHead>
            <TableHead className="text-right text-xs">Win %</TableHead>
            <TableHead className="text-right text-xs">Total P/L</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.key} data-testid={`breakdown-row-${r.key}`}>
              <TableCell className="text-xs font-medium">{r.name}</TableCell>
              <TableCell className={cn("text-right text-xs tabular-nums", pnlClass(r.long.profit))}>
                {fmtSignedMoney(r.long.profit, currency)}<span className="text-muted-foreground"> ({r.long.trades})</span>
              </TableCell>
              <TableCell className={cn("text-right text-xs tabular-nums", pnlClass(r.short.profit))}>
                {fmtSignedMoney(r.short.profit, currency)}<span className="text-muted-foreground"> ({r.short.trades})</span>
              </TableCell>
              <TableCell className="text-right text-xs tabular-nums">{r.total.trades}</TableCell>
              <TableCell className="text-right text-xs tabular-nums">{r.total.win_rate}%</TableCell>
              <TableCell className={cn("text-right text-xs font-semibold tabular-nums", pnlClass(r.total.profit))}>
                {fmtSignedMoney(r.total.profit, currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function BreakdownStatistics({ stats, currency }) {
  const [tab, setTab] = useState("symbol");
  return (
    <Panel id="breakdown-statistics" title="Breakdown statistics"
      subtitle="Long / short split by instrument and by magic number"
      testid="panel-breakdown-statistics">
      <SubTabs tabs={[{ v: "symbol", l: "By Symbol" }, { v: "magic", l: "By Magic Number" }]}
        active={tab} onChange={setTab} testidPrefix="breakdown-tab" />
      <div className="mt-3">
        {tab === "symbol"
          ? <BreakdownTable rows={stats.breakdown.by_symbol} currency={currency} keyLabel="Symbol" />
          : <BreakdownTable rows={stats.breakdown.by_magic} currency={currency} keyLabel="Magic" />}
      </div>
    </Panel>
  );
}

function net(t) {
  return (t.profit || 0) + (t.commission || 0) + (t.swap || 0);
}

function TradeHistory({ accountId, currency }) {
  const [view, setView] = useState("closed");
  const [page, setPage] = useState(1);
  const { data: closed, isLoading: loadingClosed } = useTrades(accountId, page, view === "closed");
  const { data: open, isLoading: loadingOpen } = useOpenPositions(accountId, true);

  const closedTotal = closed?.total ?? 0;
  const openTotal = open?.total ?? 0;
  const limit = closed?.limit || 25;
  const pages = Math.max(1, Math.ceil(closedTotal / limit));

  return (
    <Panel id="trade-history" title="Trade history"
      subtitle="Every closed deal and any currently open position"
      testid="panel-trade-history"
      right={
        <div className="flex gap-1 rounded-full bg-surface-2 p-1">
          <button type="button" onClick={() => setView("closed")}
            className={cn("rounded-full px-3 py-1 text-xs font-medium", view === "closed" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
            data-testid="trade-history-closed">Closed ({closedTotal})</button>
          <button type="button" onClick={() => setView("open")}
            className={cn("rounded-full px-3 py-1 text-xs font-medium", view === "open" ? "bg-primary text-primary-foreground" : "text-muted-foreground")}
            data-testid="trade-history-open">Open ({openTotal})</button>
        </div>
      }>
      {view === "closed" ? (
        <>
          {loadingClosed ? <Skeleton className="h-40 rounded-xl" /> : (
            <div className="-mx-1 overflow-x-auto px-1">
              <Table data-testid="trade-history-closed-table" className="min-w-[560px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Symbol</TableHead>
                    <TableHead className="text-xs">Side</TableHead>
                    <TableHead className="text-right text-xs">Lots</TableHead>
                    <TableHead className="text-xs">Closed</TableHead>
                    <TableHead className="text-right text-xs">Pips</TableHead>
                    <TableHead className="text-right text-xs">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(closed?.trades || []).map((t) => (
                    <TableRow key={t.trade_id}>
                      <TableCell className="text-xs font-medium">{t.symbol}</TableCell>
                      <TableCell className="text-xs">
                        <span className={cn("inline-flex items-center gap-0.5", t.type === "buy" ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]")}>
                          {t.type === "buy" ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{t.type}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{t.lots}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmtDate(t.close_time)}</TableCell>
                      <TableCell className={cn("text-right text-xs tabular-nums", pnlClass(t.pips))}>{t.pips > 0 ? "+" : ""}{t.pips}</TableCell>
                      <TableCell className={cn("text-right text-xs font-semibold tabular-nums", pnlClass(net(t)))}>{fmtSignedMoney(net(t), currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {closedTotal === 0 && !loadingClosed && <p className="py-4 text-center text-xs text-muted-foreground">No closed trades in this account.</p>}
          {pages > 1 && (
            <div className="mt-3 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Page {page} of {pages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="trade-history-prev">Prev</Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} data-testid="trade-history-next">Next</Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          {loadingOpen ? <Skeleton className="h-24 rounded-xl" /> : (
            <div className="-mx-1 overflow-x-auto px-1">
              <Table data-testid="trade-history-open-table" className="min-w-[560px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Symbol</TableHead>
                    <TableHead className="text-xs">Side</TableHead>
                    <TableHead className="text-right text-xs">Lots</TableHead>
                    <TableHead className="text-right text-xs">Open</TableHead>
                    <TableHead className="text-right text-xs">Current</TableHead>
                    <TableHead className="text-right text-xs">Floating</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(open?.positions || []).map((p) => (
                    <TableRow key={p.position_id}>
                      <TableCell className="text-xs font-medium">{p.symbol}</TableCell>
                      <TableCell className="text-xs">
                        <span className={cn("inline-flex items-center gap-0.5", p.type === "buy" ? "text-[hsl(var(--profit))]" : "text-[hsl(var(--loss))]")}>
                          {p.type === "buy" ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{p.type}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{p.lots}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{p.open_price}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{p.current_price}</TableCell>
                      <TableCell className={cn("text-right text-xs font-semibold tabular-nums", pnlClass(p.profit))}>{fmtSignedMoney(p.profit, currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {openTotal === 0 && !loadingOpen && <p className="py-4 text-center text-xs text-muted-foreground">No open positions right now.</p>}
        </>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------- default export */

export default function AdvancedStats({ accountId, range = "all", symbol = "ALL", currency = "USD", shareToken }) {
  const { data: stats, isLoading } = useAdvancedStats(accountId, range, symbol, shareToken);

  if (isLoading || !stats) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-4" data-testid="advanced-stats-view">
        <StatsStrip strip={stats.strip} currency={currency} />
        <AdvancedStatistics stats={stats} currency={currency} />
        <BreakdownStatistics stats={stats} currency={currency} />
        {!shareToken && <TradeHistory accountId={accountId} currency={currency} />}
      </div>
    </TooltipProvider>
  );
}
